import CSS from 'csstype';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  fontColorsDefault,
  getCustomTag,
  getMediaSize,
  getResponsiveStyle,
  getSpacingStyle,
  highlightedDivStyle,
  ILayoutProps,
  ISpacingProps,
  panelType,
  panelTypes,
  spacing,
} from '~/src/dash/components/layout/common';
import { PanelContext } from '~/src/dash/components/layout/contexts';
import { fontSizing } from '~/src/dash/components/text/common';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseState } from '~/src/dash/views/base/contexts';

// get reseller theme
const theme = (window as any).CONFIG.theme;

const panelTypeConfig = {
  base: {
    colors: {
      // gray scale
      black: publicaColors.black,
      veryDarkGray: publicaColors.veryDarkGray,
      darkGray: publicaColors.darkGray,
      gray: publicaColors.gray,
      lightGray: publicaColors.lightGray,
      veryLightGray: publicaColors.veryLightGray,
      white: publicaColors.white,
      // blue
      lighterBlue: publicaColors.lighterBlue,
      lightBlue: publicaColors.lightBlue,
      blue: publicaColors.blue,
      darkBlue: publicaColors.darkBlue,
      // green
      veryLightGreen: publicaColors.veryLightGreen,
      lightGreen: publicaColors.lightGreen,
      green: publicaColors.green,
      darkGreen: publicaColors.darkGreen,
      // purple
      lightPurple: publicaColors.lightPurple,
      purple: publicaColors.purple,
      // red
      veryLightRed: publicaColors.veryLightRed,
      lightRed: publicaColors.lightRed,
      red: publicaColors.red,
      // teal
      lightTeal: publicaColors.lightTeal,
      teal: publicaColors.teal,
      // yellow
      lightYellow: publicaColors.lightYellow,
      yellow: publicaColors.yellow,
    },
    fontColors: fontColorsDefault,
  },
  paper: {
    style: {
      backgroundColor: publicaColors.white,
      border: `1px solid ${publicaColors.lightGray}`,
      borderRadius: '4px',
      boxShadow: '0px 3px 4px #E2E2E2',
    },
  },
  highlighted: {
    style: highlightedDivStyle,
    fontColors: {
      muted: publicaColors.gray,
      base: publicaColors.veryDarkGray,
      highlighted: publicaColors.lightBlue,
    },
  },
  muted: {
    style: {
      backgroundColor: publicaColors.darkGray,
      border: `1px solid ${publicaColors.lightGray}`,
      borderRadius: '4px',
    },
    fontColors: {
      muted: publicaColors.lightGray,
      base: publicaColors.white,
      highlighted: publicaColors.lightTeal,
    },
  },
};

type IPanelStyleProp = Record<panelType, boolean | undefined>;

const getPanelType = ({ base, muted, highlighted, paper }: IPanelStyleProp) => {
  switch (true) {
    case paper:
      return panelTypes.PAPER;
    case muted:
      return panelTypes.MUTED;
    case highlighted:
      return panelTypes.HIGHLIGHTED;
    case base:
    default:
      return panelTypes.BASE;
  }
};

const getPanelTypeColors = ({ base, paper, muted, highlighted }: IPanelStyleProp) => {
  switch (true) {
    case paper:
    case base:
    case muted:
    case highlighted:
    default:
      return panelTypeConfig.base.colors;
  }
};

const getPanelTypeFontColors = ({ base, paper, muted, highlighted }: IPanelStyleProp) => {
  switch (true) {
    case muted:
      return panelTypeConfig.muted.fontColors;
    case highlighted:
      return panelTypeConfig.highlighted.fontColors;
    case paper:
    case base:
    default:
      return panelTypeConfig.base.fontColors;
  }
};

const getPanelTypeStyle = ({ base, paper, muted, highlighted }: IPanelStyleProp) => {
  switch (true) {
    case muted:
      return panelTypeConfig.muted.style;
    case highlighted:
      return panelTypeConfig.highlighted.style;
    case paper:
      return panelTypeConfig.paper.style;
    case base:
    default:
      return {};
  }
};

interface IPanelProps extends ILayoutProps, ISpacingProps {
  // types
  base?: boolean;
  paper?: boolean;
  muted?: boolean;
  highlighted?: boolean;
  maxHeight?: boolean;
  maxWidth?: boolean;
  max?: boolean;
  // mainly used with tabs to show/hide
  visible?: boolean;
  // custom
  // this will lock the dimensions and add vertical scrollability
  scroll?: boolean;
  // add horizontal scrollbar
  horizontalScroll?: boolean;
  // this will lock the dimensions and add horizontal and vertical scrollability
  autoScroll?: boolean;
  // use flex when you want this panel to take up any remaining empty space
  // must use on only one panel inside the 'Panels' component
  flex?: boolean;
  displayFlex?: boolean;
  enableSize?: boolean;
  // use in conjunction with enableSize when you don't need to account for collapsed/expanded navbar width
  disableNavSize?: boolean;
}

export const Panel = ({
  base,
  paper,
  muted,
  highlighted,
  maxHeight,
  maxWidth,
  max,
  visible = true,
  scroll,
  horizontalScroll,
  autoScroll,
  flex,
  displayFlex,
  enableSize,
  disableNavSize,
  noPadding,
  extraSmallPadding,
  smallPadding,
  basePadding,
  largePadding,
  extraLargePadding,
  hugePadding,
  noHorizontalPadding,
  extraSmallHorizontalPadding,
  smallHorizontalPadding,
  horizontalPadding,
  largeHorizontalPadding,
  extraLargeHorizontalPadding,
  hugeHorizontalPadding,
  noVerticalPadding,
  extraSmallVerticalPadding,
  smallVerticalPadding,
  verticalPadding,
  largeVerticalPadding,
  extraLargeVerticalPadding,
  hugeVerticalPadding,
  extraSmallPaddingV2,
  smallPaddingV2,
  basePaddingV2,
  largePaddingV2,
  extraLargePaddingV2,
  hugePaddingV2,
  noHorizontalPaddingV2,
  extraSmallHorizontalPaddingV2,
  smallHorizontalPaddingV2,
  horizontalPaddingV2,
  largeHorizontalPaddingV2,
  extraLargeHorizontalPaddingV2,
  hugeHorizontalPaddingV2,
  noVerticalPaddingV2,
  extraSmallVerticalPaddingV2,
  smallVerticalPaddingV2,
  verticalPaddingV2,
  largeVerticalPaddingV2,
  extraLargeVerticalPaddingV2,
  hugeVerticalPaddingV2,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  style,
  responsive,
  extraLargeStyle,
  largeStyle,
  mediumStyle,
  smallStyle,
  extraSmallStyle,
  children,
  innerRef,
  className = '',
  tag,
  cypressID,
}: IPanelProps) => {
  const ref = useRef(null);
  const isMounted = useRef(false);
  const [size, setSize] = useState([null, null]);
  const [width, height] = size;
  const { isNavExpanded } = useBaseState();
  const mediaSize = responsive ? useBaseState().mediaSize : 'extraSmall';

  const updateSize = () => {
    // @ts-ignore
    const currentWidth = ref.current ? ref.current.clientWidth : 0;
    // @ts-ignore
    const currentHeight = ref.current ? ref.current.clientHeight : 0;
    setSize([currentWidth, currentHeight]);
  };

  // updates dimensions on window resize
  useLayoutEffect(() => {
    if (enableSize) {
      updateSize();
      window.addEventListener('resize', updateSize);
    }
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // this updates the width whenever the navbar collapses/expands
  useEffect(() => {
    if (enableSize && isMounted.current && !disableNavSize) {
      setTimeout(() => {
        updateSize();
      }, 500);
    } else {
      isMounted.current = true;
    }
  }, [isNavExpanded]);

  const typeProps = {
    base,
    paper,
    muted,
    highlighted,
  };
  const type = getPanelType(typeProps);
  const colors = getPanelTypeColors(typeProps);
  const fontColors = getPanelTypeFontColors(typeProps);
  const panelTypeStyle = getPanelTypeStyle(typeProps);

  const addOptionalStyles = () => {
    const styles = {} as CSS.Properties;
    if (maxHeight || max) {
      styles.height = '100%';
    }
    if (maxWidth || max) {
      styles.width = '100%';
    }
    if (!visible) {
      styles.display = 'none';
    }
    if (scroll) {
      styles.height = '100%';
      styles.overflowX = 'hidden';
      styles.overflowY = 'auto';
    }
    if (horizontalScroll) {
      styles.overflowX = 'auto';
    }
    if (autoScroll) {
      styles.height = '100%';
      styles.width = '100%';
      styles.overflowX = 'auto';
      styles.overflowY = 'auto';
      styles.display = 'flex';
    }
    if (flex) {
      styles.flex = '1 1 0%';
    }
    if (displayFlex) {
      styles.display = 'flex';
    }
    return styles;
  };

  const panelSizing = enableSize
    ? {
        width: width || 0,
        height: height || 0,
        panelSize: getMediaSize(width),
      }
    : {};

  return (
    <PanelContext.Provider
      value={{
        colors,
        fontColors,
        type,
        theme,
        ...panelSizing,
      }}
    >
      <div
        ref={
          innerRef
            ? (element: HTMLDivElement) => {
                innerRef(element);
                // @ts-ignore
                ref(element);
              }
            : ref
        }
        className={className}
        data-cy={cypressID}
        style={{
          ...getCustomTag('PANEL', tag),
          position: 'relative',
          boxSizing: 'border-box',
          fontSize: fontSizing.base,
          color: fontColors.base,
          ...panelTypeStyle,
          ...addOptionalStyles(),
          ...getSpacingStyle({
            defaultPadding: spacing.none,
            noPadding,
            extraSmallPadding,
            smallPadding,
            basePadding,
            largePadding,
            extraLargePadding,
            hugePadding,
            noHorizontalPadding,
            extraSmallHorizontalPadding,
            smallHorizontalPadding,
            horizontalPadding,
            largeHorizontalPadding,
            extraLargeHorizontalPadding,
            hugeHorizontalPadding,
            noVerticalPadding,
            extraSmallVerticalPadding,
            smallVerticalPadding,
            verticalPadding,
            largeVerticalPadding,
            extraLargeVerticalPadding,
            hugeVerticalPadding,
            extraSmallPaddingV2,
            smallPaddingV2,
            basePaddingV2,
            largePaddingV2,
            extraLargePaddingV2,
            hugePaddingV2,
            noHorizontalPaddingV2,
            extraSmallHorizontalPaddingV2,
            smallHorizontalPaddingV2,
            horizontalPaddingV2,
            largeHorizontalPaddingV2,
            extraLargeHorizontalPaddingV2,
            hugeHorizontalPaddingV2,
            noVerticalPaddingV2,
            extraSmallVerticalPaddingV2,
            smallVerticalPaddingV2,
            verticalPaddingV2,
            largeVerticalPaddingV2,
            extraLargeVerticalPaddingV2,
            hugeVerticalPaddingV2,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
            marginTop,
            marginRight,
            marginBottom,
            marginLeft,
          }),
          ...style,
          ...getResponsiveStyle({
            responsive,
            mediaSize,
            extraLargeStyle,
            largeStyle,
            mediumStyle,
            smallStyle,
            extraSmallStyle,
          }),
        }}
      >
        {children}
      </div>
    </PanelContext.Provider>
  );
};
